import { FC } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from "@heroicons/react/24/solid";

interface PaymentStatusProps {
  messageState: string;
  onConfirm: () => void;
  onExploreLocations: () => void;
  onCancel: () => void;
  isConfirmLoading: boolean;
  isCancelLoading: boolean;
}

const PaymentStatus: FC<PaymentStatusProps> = ({
  messageState,
  onConfirm,
  onExploreLocations,
  onCancel,
  isConfirmLoading,
  isCancelLoading,
}) => {
  const getStatusConfig = () => {
    switch (messageState) {
      case "Payment completed.":
        return {
          title: "Réservation envoyée avec succès !",
          icon: CheckCircleIcon,
          iconColor: "text-emerald-500",
          bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
          borderColor: "border-emerald-200 dark:border-emerald-800",
        };
      case "Payment not yet completed.":
        return {
          title: "Confirmation requise",
          icon: ClockIcon,
          iconColor: "text-amber-500",
          bgColor: "bg-amber-50 dark:bg-amber-900/20",
          borderColor: "border-amber-200 dark:border-amber-800",
        };
      case "no reservation with this reference":
        return {
          title: "Erreur de réservation",
          icon: ExclamationTriangleIcon,
          iconColor: "text-red-500",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          borderColor: "border-red-200 dark:border-red-800",
        };
      default:
        return {
          title: "",
          icon: ClockIcon,
          iconColor: "text-gray-500",
          bgColor: "bg-gray-50 dark:bg-gray-900/20",
          borderColor: "border-gray-200 dark:border-gray-800",
        };
    }
  };

  const statusConfig = getStatusConfig();

  const getStatusDescription = () => {
    switch (messageState) {
      case "Payment completed.":
        return {
          title:
            "Votre réservation ne sera pas confirmée tant que l'hôte n'aura pas accepté votre demande (dans un délai de 48 heures).",
          subtitle: (
            <>
              Veuillez noter que le montant sera{" "}
              <span className="font-semibold text-secondary-6000">entièrement remboursé</span> si l'hôte ne
              confirme pas la réservation
            </>
          ),
        };
      case "Payment not yet completed.":
        return {
          title: "Veuillez confirmer votre réservation",
          subtitle: null,
        };
      case "no reservation with this reference":
        return {
          title: "Erreur survenue ou pas de réservation avec ce code",
          subtitle: null,
        };
      default:
        return { title: "", subtitle: null };
    }
  };

  const renderActions = () => {
    switch (messageState) {
      case "Payment completed.":
        return (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ButtonPrimary
              onClick={onExploreLocations}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Explorer d'autres espaces
              </span>
            </ButtonPrimary>
          </div>
        );
      case "Payment not yet completed.":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <ButtonPrimary
                onClick={onConfirm}
                loading={isConfirmLoading}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-2">
                  {!isConfirmLoading && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  Confirmer la réservation
                </span>
              </ButtonPrimary>
              <button
                onClick={onCancel}
                disabled={isCancelLoading}
                className="px-6 py-3 border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 rounded-xl font-medium transition-all duration-300 hover:shadow-md disabled:opacity-50"
              >
                <span className="flex items-center gap-2">
                  {isCancelLoading ? (
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                  Annuler
                </span>
              </button>
            </div>
          </div>
        );
      case "no reservation with this reference":
        return (
          <div className="flex justify-center">
            <ButtonPrimary
              onClick={onExploreLocations}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Explorer d'autres espaces
            </ButtonPrimary>
          </div>
        );
      default:
        return null;
    }
  };

  const description = getStatusDescription();

  const IconComponent = statusConfig.icon;

  return (
    <div className="space-y-8">
      {/* Status Header */}
      <div
        className={`${statusConfig.bgColor} ${statusConfig.borderColor} border-2 rounded-2xl p-6 text-center transition-all duration-300`}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className={`p-3 rounded-full ${statusConfig.bgColor} ${statusConfig.borderColor} border`}>
            <IconComponent className={`w-12 h-12 ${statusConfig.iconColor}`} />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">{statusConfig.title}</h2>
            {description.title && (
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">{description.title}</p>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {description.subtitle && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-100 dark:border-blue-800/50">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-gray-800 dark:text-gray-200 leading-relaxed">{description.subtitle}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="pt-4">{renderActions()}</div>
    </div>
  );
};

export default PaymentStatus;
